import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import {
  MessageSquare, Trophy, Target, Lightbulb, Send, Heart,
  Calendar, ArrowLeft, Sprout, Lock, Users, ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CommunityPortal = () => {
  const [email, setEmail] = useState('');
  const [isMember, setIsMember] = useState(false);
  const [member, setMember] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [activeTab, setActiveTab] = useState('feed');

  // Data
  const [feed, setFeed] = useState([]);
  const [tips, setTips] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [wins, setWins] = useState([]);
  const [qas, setQas] = useState([]);
  const [calendar, setCalendar] = useState(null);

  // Posting states
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replyTarget, setReplyTarget] = useState(null);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [isPosting, setIsPosting] = useState(false);

  const verifyMembership = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: 'Invalid Email', description: 'Enter a valid email.', variant: 'destructive' });
      return;
    }
    setIsVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/community/verify/${encodeURIComponent(email)}`);
      const data = await res.json();
      if (data.is_member) {
        setIsMember(true);
        setMember(data.member);
        loadAllData();
      } else {
        toast({ title: 'Not a Member', description: 'No active community subscription found for this email.', variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to verify. Try again.', variant: 'destructive' });
    } finally {
      setIsVerifying(false);
    }
  };

  const loadAllData = async () => {
    try {
      const [feedRes, tipsRes, challengeRes, winsRes, qasRes, calRes] = await Promise.all([
        fetch(`${API_URL}/api/community/feed?limit=50`),
        fetch(`${API_URL}/api/community/feed?post_type=tip&limit=10`),
        fetch(`${API_URL}/api/community/challenge`),
        fetch(`${API_URL}/api/community/feed?post_type=win&limit=10`),
        fetch(`${API_URL}/api/community/feed?post_type=qa&limit=10`),
        fetch(`${API_URL}/api/community/calendar`),
      ]);
      const [feedData, tipsData, challengeData, winsData, qasData, calData] = await Promise.all([
        feedRes.json(), tipsRes.json(), challengeRes.json(), winsRes.json(), qasRes.json(), calRes.json()
      ]);
      setFeed(feedData.posts || []);
      setTips(tipsData.posts || []);
      setChallenge(challengeData.challenge);
      setWins(winsData.posts || []);
      setQas(qasData.posts || []);
      setCalendar(calData.calendar);
    } catch (err) {
      console.error('Failed to load community data:', err);
    }
  };

  const handlePost = async (type) => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      toast({ title: 'Missing Fields', description: 'Title and content are required.', variant: 'destructive' });
      return;
    }
    setIsPosting(true);
    try {
      const res = await fetch(`${API_URL}/api/community/post/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, title: newPostTitle, content: newPostContent })
      });
      if (!res.ok) throw new Error('Failed');
      toast({ title: 'Posted!', description: 'Your post is live.' });
      setNewPostTitle('');
      setNewPostContent('');
      loadAllData();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to post. Try again.', variant: 'destructive' });
    } finally {
      setIsPosting(false);
    }
  };

  const handleReply = async (postTitle) => {
    if (!replyContent.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/community/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, post_title: postTitle, content: replyContent })
      });
      if (!res.ok) throw new Error('Failed');
      setReplyContent('');
      setReplyTarget(null);
      loadAllData();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to reply.', variant: 'destructive' });
    }
  };

  const handleLike = async (postTitle) => {
    try {
      await fetch(`${API_URL}/api/community/like?email=${encodeURIComponent(email)}&post_title=${encodeURIComponent(postTitle)}`, { method: 'POST' });
      loadAllData();
    } catch (err) {}
  };

  const toggleExpand = (title) => {
    setExpandedPosts(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'tip': return <Lightbulb className="w-4 h-4 text-amber-500" />;
      case 'challenge': return <Target className="w-4 h-4 text-[#C17855]" />;
      case 'win': return <Trophy className="w-4 h-4 text-emerald-500" />;
      case 'qa': return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'calendar': return <Sprout className="w-4 h-4 text-[#6B7A5C]" />;
      default: return null;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'tip': return 'Weekly Tip';
      case 'challenge': return 'Monthly Challenge';
      case 'win': return 'Member Win';
      case 'qa': return 'Q&A';
      case 'calendar': return 'Growing Calendar';
      default: return type;
    }
  };

  const PostCard = ({ post }) => {
    const isExpanded = expandedPosts[post.title];
    const hasReplies = post.replies && post.replies.length > 0;

    return (
      <Card className="bg-white border border-[#E5DFD0] shadow-sm hover:shadow-md transition-shadow" data-testid={`post-card-${post.type}`}>
        <div className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                post.type === 'tip' ? 'bg-amber-50' : post.type === 'challenge' ? 'bg-[#C17855] bg-opacity-10' :
                post.type === 'win' ? 'bg-emerald-50' : post.type === 'qa' ? 'bg-blue-50' : 'bg-[#EBE6D8]'
              }`}>
                {getTypeIcon(post.type)}
              </div>
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-[#A89884]">{getTypeLabel(post.type)}</span>
                <p className="text-xs text-[#A89884]">{post.author} &middot; {formatDate(post.created_at)}</p>
              </div>
            </div>
            {post.pinned && (
              <span className="text-xs bg-[#6B7A5C] text-white px-2 py-0.5 rounded-full">Pinned</span>
            )}
          </div>

          <h3 className="text-lg font-bold text-[#2C1810] mb-2">{post.title}</h3>
          <p className="text-[#5A5046] text-sm leading-relaxed whitespace-pre-line">{post.content}</p>

          {/* Challenge stats */}
          {post.type === 'challenge' && post.participants && (
            <div className="flex gap-4 mt-4 p-3 bg-[#EBE6D8] rounded-lg">
              <div className="text-center">
                <p className="text-xl font-bold text-[#C17855]">{post.participants}</p>
                <p className="text-xs text-[#5A5046]">Participants</p>
              </div>
              <div className="text-center">
                <p className="text-xl font-bold text-[#6B7A5C]">${post.total_saved?.toLocaleString()}</p>
                <p className="text-xs text-[#5A5046]">Total Saved</p>
              </div>
            </div>
          )}

          {/* Calendar zones */}
          {post.type === 'calendar' && post.zones && (
            <div className="mt-4 space-y-3">
              {Object.entries(post.zones).map(([zone, data]) => (
                <div key={zone} className="bg-[#EBE6D8] p-3 rounded-lg">
                  <p className="text-sm font-bold text-[#2C1810] mb-1">Zone {zone}</p>
                  <p className="text-xs text-[#5A5046]"><strong>Plant now:</strong> {data.plant_now}</p>
                  <p className="text-xs text-[#5A5046]"><strong>Harvest:</strong> {data.harvest}</p>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#E5DFD0]">
            <button onClick={() => handleLike(post.title)} className="flex items-center gap-1 text-sm text-[#A89884] hover:text-[#C17855] transition-colors">
              <Heart className="w-4 h-4" /> {post.likes || 0}
            </button>
            {hasReplies && (
              <button onClick={() => toggleExpand(post.title)} className="flex items-center gap-1 text-sm text-[#A89884] hover:text-[#6B7A5C] transition-colors">
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {post.replies.length} {post.replies.length === 1 ? 'reply' : 'replies'}
              </button>
            )}
            <button onClick={() => setReplyTarget(replyTarget === post.title ? null : post.title)} className="flex items-center gap-1 text-sm text-[#A89884] hover:text-[#6B7A5C] transition-colors">
              <MessageSquare className="w-4 h-4" /> Reply
            </button>
          </div>

          {/* Replies */}
          {isExpanded && hasReplies && (
            <div className="mt-3 space-y-2 pl-4 border-l-2 border-[#E5DFD0]">
              {post.replies.map((reply, i) => (
                <div key={i} className="py-2">
                  <p className="text-xs font-semibold text-[#2C1810]">{reply.author} <span className="font-normal text-[#A89884]">&middot; {formatDate(reply.created_at)}</span></p>
                  <p className="text-sm text-[#5A5046] mt-1">{reply.content}</p>
                </div>
              ))}
            </div>
          )}

          {/* Reply input */}
          {replyTarget === post.title && (
            <div className="mt-3 flex gap-2">
              <Input
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                className="flex-1 text-sm border-[#E5DFD0]"
                onKeyDown={(e) => e.key === 'Enter' && handleReply(post.title)}
                data-testid="reply-input"
              />
              <Button onClick={() => handleReply(post.title)} className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-3" data-testid="reply-submit-btn">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>
    );
  };

  // ==========================================
  // LOGIN GATE
  // ==========================================
  if (!isMember) {
    return (
      <>
        <div className="min-h-screen bg-[#F5F1E8]">
          <header className="bg-white shadow-sm px-6 py-4">
            <div className="max-w-4xl mx-auto flex items-center gap-4">
              <Link to="/" className="text-[#A89884] hover:text-[#2C1810]"><ArrowLeft className="w-5 h-5" /></Link>
              <h1 className="text-xl font-bold"><span className="text-[#2C1810]">Spend</span><span className="text-[#6B7A5C]">Less</span> <span className="text-[#A89884] font-normal">Community</span></h1>
            </div>
          </header>

          <main className="pt-12 pb-20 px-6">
            <div className="max-w-md mx-auto">
              <Card className="bg-white border-none shadow-lg p-10" data-testid="community-login-card">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#6B7A5C] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-[#6B7A5C]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#2C1810] mb-2">Member Access</h2>
                  <p className="text-[#5A5046] text-sm mb-6">Enter your email to access the community.</p>
                  <form onSubmit={verifyMembership} className="space-y-4">
                    <Input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com" className="w-full px-5 py-3 rounded-lg border-[#E5DFD0]"
                      disabled={isVerifying} data-testid="community-login-email"
                    />
                    <Button type="submit" disabled={isVerifying} className="w-full bg-[#6B7A5C] hover:bg-[#5A6850] text-white py-3 rounded-lg" data-testid="community-login-btn">
                      {isVerifying ? 'Verifying...' : 'Enter Community'}
                    </Button>
                  </form>
                  <div className="mt-8 pt-6 border-t border-[#E5DFD0]">
                    <p className="text-[#5A5046] text-sm mb-3">Not a member yet?</p>
                    <Link to="/products/community">
                      <Button variant="outline" className="border-2 border-[#C17855] text-[#C17855] hover:bg-[#C17855] hover:text-white px-6 py-2 rounded-lg" data-testid="community-join-btn">
                        Join — $9/mo
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </div>
          </main>
        </div>
        <Toaster />
      </>
    );
  }

  // ==========================================
  // MEMBER PORTAL
  // ==========================================
  const tabs = [
    { id: 'feed', label: 'All Posts', icon: MessageSquare },
    { id: 'tips', label: 'Weekly Tips', icon: Lightbulb },
    { id: 'challenge', label: 'Challenge', icon: Target },
    { id: 'wins', label: 'Wins', icon: Trophy },
    { id: 'qa', label: 'Q&A', icon: MessageSquare },
    { id: 'calendar', label: 'Growing', icon: Sprout },
  ];

  const currentPosts = activeTab === 'feed' ? feed :
    activeTab === 'tips' ? tips :
    activeTab === 'challenge' ? (challenge ? [challenge] : []) :
    activeTab === 'wins' ? wins :
    activeTab === 'qa' ? qas :
    activeTab === 'calendar' ? (calendar ? [calendar] : []) : [];

  return (
    <>
      <div className="min-h-screen bg-[#F5F1E8]">
        {/* Header */}
        <header className="bg-white shadow-sm px-6 py-4 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-[#A89884] hover:text-[#2C1810]"><ArrowLeft className="w-5 h-5" /></Link>
              <h1 className="text-xl font-bold">
                <span className="text-[#2C1810]">Spend</span><span className="text-[#6B7A5C]">Less</span>
                <span className="text-[#A89884] font-normal ml-2">Community</span>
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#6B7A5C] text-white flex items-center justify-center text-sm font-bold">
                {member?.display_name?.[0]?.toUpperCase() || '?'}
              </div>
              <span className="text-sm text-[#5A5046] hidden sm:block">{member?.display_name}</span>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 py-6">
          {/* Tabs */}
          <div className="flex gap-1 mb-6 overflow-x-auto pb-2" data-testid="community-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#6B7A5C] text-white shadow-sm'
                    : 'bg-white text-[#5A5046] hover:bg-[#EBE6D8] border border-[#E5DFD0]'
                }`}
                data-testid={`community-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* New post form (for wins and Q&A) */}
          {(activeTab === 'wins' || activeTab === 'qa') && (
            <Card className="bg-white border border-[#E5DFD0] p-5 mb-6" data-testid="new-post-form">
              <h3 className="text-sm font-bold text-[#2C1810] mb-3">
                {activeTab === 'wins' ? 'Share a Win' : 'Ask the Community'}
              </h3>
              <div className="space-y-3">
                <Input
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                  placeholder={activeTab === 'wins' ? 'What did you accomplish?' : 'Your question'}
                  className="border-[#E5DFD0] text-sm"
                  data-testid="new-post-title"
                />
                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder={activeTab === 'wins' ? 'Tell us the details! How much did you save? What did you learn?' : 'Add details, context, what you\'ve tried...'}
                  className="w-full p-3 border border-[#E5DFD0] rounded-lg text-sm text-[#5A5046] resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#6B7A5C]"
                  data-testid="new-post-content"
                />
                <Button
                  onClick={() => handlePost(activeTab === 'wins' ? 'win' : 'qa')}
                  disabled={isPosting}
                  className="bg-[#C17855] hover:bg-[#B06745] text-white text-sm px-6"
                  data-testid="new-post-submit"
                >
                  {isPosting ? 'Posting...' : activeTab === 'wins' ? 'Share Win' : 'Post Question'}
                </Button>
              </div>
            </Card>
          )}

          {/* Posts */}
          <div className="space-y-4" data-testid="community-posts">
            {currentPosts.length > 0 ? (
              currentPosts.map((post, i) => <PostCard key={i} post={post} />)
            ) : (
              <div className="text-center py-12 text-[#A89884]">
                <p>No posts yet in this category.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Toaster />
    </>
  );
};

export default CommunityPortal;
